import {
  useEffect, useRef, useState, useMemo, useCallback
} from 'react';
import PropTypes from 'prop-types';
import AutoScroller from '@/components/app/AutoScroller';
import AddIcon from '@/components/component-library/Icon/types/Add';
import TickIcon from '@/components/component-library/Icon/types/Tick';
import ShareIcon from '@/components/component-library/Icon/types/Share';
import Gradient, { GRADIENT_POSITION_LEFT, GRADIENT_POSITION_RIGHT } from '@/components/app/Gradient';
import Text, { FONT_SIZE_040, FONT_SIZE_050 } from '@/components/component-library/Text';
import { BLACK_MATTE } from '@/config/colors';
import useCurrentLayout from '@/hooks/layout/useCurrentLayout';
import { classes } from '@/helpers/styling';

const TEXT_SCROLL_PX_PER_SECOND = 25;
const TEXT_GAP_PERCENTAGE = 15;
const PADDING_RIGHT_PX = 16;

const COMMON_GRADIENT_PROPS = {
  colour: BLACK_MATTE,
  style: {
    width: '16px',
    height: 'calc(100% + 5px)'
  },
  wrapperStyle: { zIndex: 2 },
  gradientBreadth: '90%'
};

const LEFT_GRADIENT_STYLE = {
  ...COMMON_GRADIENT_PROPS.style,
  left: '-1px'
};

const EPISODE_AUTO_SCROLLER_STYLE = {
  height: '20px',
  paddingBottom: '2px',
  width: '100%'
};

const SHOW_AUTO_SCROLLER_STYLE = {
  height: '16px',
  paddingBottom: '2px',
  width: '100%'
};

const AUTO_SCROLLER_CONTENT_STYLE = {};
const AUTO_SCROLLER_CONTENT_STYLE_WITH_GRADIENTS = { padding: `0 0 0 ${ PADDING_RIGHT_PX }px` };

const TrackInfoScroller = ({
  episodeTitle,
  channelTitle,
  onClickLibrary,
  libraryActionType,
  libraryActionLoading,
  autoScrollerProps,
  disableClicks,
  className,
  onClickChannel,
  onClickEpisode,
  onClickShare,
  disableLibraryButton,
  disableGradients,
  libraryLoading
}) => {
  const [forceEpisodeScroll, setForceEpisodeScroll] = useState(false);
  const [forceShowScroll, setForceShowScroll] = useState(false);
  const [scrollEpisode, setScrollEpisode] = useState(true);
  const [scrollShow, setScrollShow] = useState(true);
  const [topWidth, setTopWidth] = useState(120);
  const [bottomWidth, setBottomWidth] = useState(120);
  const wrapperRef = useRef(null);

  const { browserSize } = useCurrentLayout();

  useEffect(() => {
    if (bottomWidth && topWidth) {
      // Can decrease width of autoscroller
      wrapperRef.current.style.width = `${ Math.max(topWidth, bottomWidth) + (disableGradients ? 0 : (PADDING_RIGHT_PX - 1)) }px`;
    }
  }, [scrollEpisode, scrollShow, bottomWidth, topWidth, disableGradients]);

  useEffect(() => {
    setForceShowScroll(false);
    setForceEpisodeScroll(false);
  }, [browserSize]);

  const handleCalculateEpisodeWidth = useCallback((width, contentNeedsScrolling) => {
    setTopWidth(width);
    setScrollEpisode(contentNeedsScrolling);
  }, []);

  const handleCalculateShowWidth = useCallback((width, contentNeedsScrolling) => {
    setBottomWidth(width);
    setScrollShow(contentNeedsScrolling);
  }, []);

  const handleHoverEpisodeTitle = useCallback((e) => {
    e.preventDefault();

    setForceEpisodeScroll(true);
  }, []);

  const handleCompleteEpisodeScroll = useCallback(() => {
    setForceEpisodeScroll(false);
  }, []);

  const handleHoverShowTitle = useCallback((e) => {
    e.preventDefault();

    setForceShowScroll(true);
  }, []);

  const handleCompleteShowScroll = useCallback(() => {
    setForceShowScroll(false);
  }, []);

  const scrollDuration = topWidth / TEXT_SCROLL_PX_PER_SECOND;
  const libraryButtonDisabled = libraryLoading || libraryActionLoading;

  const matchSpeedParams = useMemo(() => ({
    width: topWidth + 2,
    duration: scrollDuration
  }), [topWidth, scrollDuration]);

  const episodeScrollerContentProps = useMemo(() => ({ onMouseOver: disableClicks ? undefined : handleHoverEpisodeTitle }), [disableClicks, handleHoverEpisodeTitle]);
  const showScrollerContentProps = useMemo(() => ({ onMouseOver: disableClicks ? undefined : handleHoverShowTitle }), [disableClicks, handleHoverShowTitle]);

  const disableClickEpisode = (disableClicks || !onClickEpisode);
  const disableClickChannel = (disableClicks || !onClickChannel);

  return (
    <>
      <div
        className={ classes('player-bar-current-track-info', className) }
        ref={ wrapperRef }
      >
        { !disableGradients && (
        <Gradient
          { ...COMMON_GRADIENT_PROPS }
          position={ GRADIENT_POSITION_LEFT }
          style={ LEFT_GRADIENT_STYLE }
        />
        ) }
        <div style={ {
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          height: '100%'
        } }
        >
          { episodeTitle && (
            <AutoScroller
              autoscroll
              contentProps={ episodeScrollerContentProps }
              contentStyle={ disableGradients ? AUTO_SCROLLER_CONTENT_STYLE : AUTO_SCROLLER_CONTENT_STYLE_WITH_GRADIENTS }
              forceScroll={ forceEpisodeScroll }
              key={ `episode${ episodeTitle }${ browserSize }` }
              onCalculateWidth={ handleCalculateEpisodeWidth }
              onCompleteScroll={ handleCompleteEpisodeScroll }
              percentageGap={ TEXT_GAP_PERCENTAGE }
              scrollDuration={ scrollDuration }
              style={ EPISODE_AUTO_SCROLLER_STYLE }
              { ...autoScrollerProps }
            >
              <Text
                bold
                className={ classes('episode-title', { '--disable-clicks': disableClickEpisode }) }
                onClick={ disableClickEpisode ? undefined : onClickEpisode }
                size={ FONT_SIZE_050 }
                useHeaderFont
              >
                { episodeTitle }
              </Text>
            </AutoScroller>
          ) }
          { channelTitle && (
            <AutoScroller
              autoscroll
              contentProps={ showScrollerContentProps }
              contentStyle={ disableGradients ? AUTO_SCROLLER_CONTENT_STYLE : AUTO_SCROLLER_CONTENT_STYLE_WITH_GRADIENTS }
              forceScroll={ forceShowScroll }
              key={ `show${ channelTitle }${ browserSize }` }
              matchSpeedParams={ matchSpeedParams }
              onCalculateWidth={ handleCalculateShowWidth }
              onCompleteScroll={ handleCompleteShowScroll }
              percentageGap={ TEXT_GAP_PERCENTAGE }
              style={ SHOW_AUTO_SCROLLER_STYLE }
              { ...autoScrollerProps }
            >
              <Text
                bold
                className={ classes('channel-title', { '--disable-clicks': disableClickChannel }) }
                fixed
                onClick={ disableClickChannel ? undefined : onClickChannel }
                size={ FONT_SIZE_040 } useHeaderFont
              >
                { channelTitle }
              </Text>
            </AutoScroller>
          ) }
        </div>
        { !disableGradients && (
        <Gradient
          { ...COMMON_GRADIENT_PROPS }
          directionReverse
          position={ GRADIENT_POSITION_RIGHT }
        />
        ) }
      </div>
      { !disableLibraryButton && !!onClickLibrary && episodeTitle && channelTitle && (
        <div
          className={ classes(
            'library-button',
            ((libraryActionType === 'add') ? 'add' : 'tick'),
            { '--is-disabled': libraryButtonDisabled }
          ) }
          onClick={ libraryButtonDisabled ? undefined : onClickLibrary }
        >
          { (libraryActionType === 'add') ? <AddIcon /> : <TickIcon /> }
        </div>
      ) }
      { onClickShare && episodeTitle && channelTitle && (
        <div
          className='share-button'
          onClick={ onClickShare }
        >
          <ShareIcon />
        </div>
      ) }
    </>
  );
};

TrackInfoScroller.propTypes = {
  episodeTitle: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string
  ]),
  channelTitle: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string
  ]),
  onClickLibrary: PropTypes.func,
  libraryActionType: PropTypes.string,
  libraryActionLoading: PropTypes.bool,
  autoScrollerProps: PropTypes.object,
  disableClicks: PropTypes.bool,
  className: PropTypes.string,
  onClickChannel: PropTypes.func,
  onClickEpisode: PropTypes.func,
  onClickShare: PropTypes.func,
  disableLibraryButton: PropTypes.bool,
  disableGradients: PropTypes.bool,
  libraryLoading: PropTypes.bool
};

TrackInfoScroller.defaultProps = {
  episodeTitle: '',
  channelTitle: '',
  onClickLibrary: undefined,
  libraryActionType: null,
  libraryActionLoading: false,
  autoScrollerProps: {},
  disableClicks: false,
  className: '',
  onClickChannel: undefined,
  onClickEpisode: undefined,
  onClickShare: undefined,
  disableLibraryButton: false,
  disableGradients: false,
  libraryLoading: false
};

export default TrackInfoScroller;